import {motion, Variants} from 'framer-motion';
import {memo, PropsWithChildren, useEffect, useCallback} from 'react';
import {useWizard} from 'react-use-wizard';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';
import {AssetIconChevron} from '../../../../../../assets';

export type GetStartedWizardStepContainerProps = PropsWithChildren & {
  previousStep: React.MutableRefObject<number>;
};

const variants: Variants = {
  enter: (direction: number) => {
    return {
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    };
  },
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${Palette.primarySubtleGreen};
  width: min(580px, 90vw);
  padding: 48px 28px 28px 28px;
  border-radius: 24px;
  box-shadow:
    hsla(220, 30%, 5%, 0.1) 0px 5px 15px 0px,
    hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px;
  align-items: center;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  background: none;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  transform: rotate(180deg); /* Rotate to make it point left */

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;

export const GetStartedWizardStepContainer = memo(
  ({children, previousStep}: GetStartedWizardStepContainerProps) => {
    const {
      activeStep,
      previousStep: goToPreviousStep,
      isFirstStep,
    } = useWizard();

    useEffect(() => {
      return () => {
        previousStep.current = activeStep;
      };
    }, [activeStep, previousStep]);

    const handleBack = useCallback(() => {
      goToPreviousStep();
    }, [goToPreviousStep]);

    return (
      <motion.div
        custom={activeStep - previousStep.current}
        variants={variants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Container>
          {!isFirstStep && (
            <BackButton onClick={handleBack}>
              <AssetIconChevron size={20} color={Palette.almostBlack} />
            </BackButton>
          )}
          {children}
        </Container>
      </motion.div>
    );
  },
);
